if (!customElements.get('alberobello-carousel')) {
  customElements.define(
    'alberobello-carousel',
    class AlberobelloCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.alberobello-quality-plan__track');
        if (!this.track) return;

        this.dragState = null;
        this.rafId = null;
        this.DRAG_THRESHOLD = 4;
        this.SNAP_DURATION = 600;
        this.VELOCITY_THRESHOLD = 0.3;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);

        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('click', this.onClickCapture, true);
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.cancelAnimation();
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('click', this.onClickCapture, true);
      }

      onPointerDown(e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        this.cancelAnimation();
        this.dragState = {
          startX: e.clientX,
          startScrollLeft: this.track.scrollLeft,
          moved: false,
          pointerId: e.pointerId,
          samples: [{ x: e.clientX, t: performance.now() }],
        };
        this.track.setPointerCapture(e.pointerId);
        this.track.addEventListener('pointermove', this.onPointerMove);
        this.track.addEventListener('pointerup', this.onPointerUp);
        this.track.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(e) {
        if (!this.dragState || e.pointerId !== this.dragState.pointerId) return;
        const dx = e.clientX - this.dragState.startX;
        if (!this.dragState.moved && Math.abs(dx) > this.DRAG_THRESHOLD) {
          this.dragState.moved = true;
          this.track.classList.add('is-dragging');
        }
        if (this.dragState.moved) {
          this.track.scrollLeft = this.dragState.startScrollLeft - dx;
          const now = performance.now();
          this.dragState.samples.push({ x: e.clientX, t: now });
          while (
            this.dragState.samples.length > 1 &&
            now - this.dragState.samples[0].t > 100
          ) {
            this.dragState.samples.shift();
          }
          e.preventDefault();
        }
      }

      onPointerUp(e) {
        if (!this.dragState || e.pointerId !== this.dragState.pointerId) return;
        const moved = this.dragState.moved;
        const samples = this.dragState.samples;
        this.track.removeEventListener('pointermove', this.onPointerMove);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
        try { this.track.releasePointerCapture(e.pointerId); } catch (_) {}
        this.track.classList.remove('is-dragging');
        this.suppressClick = moved;
        this.dragState = null;
        if (moved) {
          setTimeout(() => { this.suppressClick = false; }, 0);
          this.snapAfterDrag(this.computeScrollVelocity(samples));
        }
      }

      onClickCapture(e) {
        if (this.suppressClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      }

      computeScrollVelocity(samples) {
        if (!samples || samples.length < 2) return 0;
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        if (dt <= 0) return 0;
        return -(last.x - first.x) / dt;
      }

      snapAfterDrag(velocity) {
        const slides = this.track.children;
        if (!slides.length) return;
        const current = this.track.scrollLeft;

        let nearestIdx = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < slides.length; i++) {
          const d = Math.abs(slides[i].offsetLeft - current);
          if (d < nearestDist) {
            nearestDist = d;
            nearestIdx = i;
          }
        }

        let targetIdx = nearestIdx;
        if (Math.abs(velocity) > this.VELOCITY_THRESHOLD) {
          const direction = velocity > 0 ? 1 : -1;
          const nearestOffset = slides[nearestIdx].offsetLeft;
          if (direction > 0 && nearestOffset < current && nearestIdx + 1 < slides.length) {
            targetIdx = nearestIdx + 1;
          } else if (direction < 0 && nearestOffset > current && nearestIdx - 1 >= 0) {
            targetIdx = nearestIdx - 1;
          }
        }

        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const target = Math.max(0, Math.min(slides[targetIdx].offsetLeft, max));
        this.animateScrollTo(target, this.SNAP_DURATION);
      }

      animateScrollTo(target, duration) {
        this.cancelAnimation();
        const start = this.track.scrollLeft;
        const delta = target - start;
        if (Math.abs(delta) < 1) return;

        const startTime = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        this.track.classList.add('is-animating');

        const tick = (now) => {
          const t = Math.min((now - startTime) / duration, 1);
          this.track.scrollLeft = start + delta * ease(t);
          if (t < 1) {
            this.rafId = requestAnimationFrame(tick);
          } else {
            this.rafId = null;
            this.track.classList.remove('is-animating');
          }
        };
        this.rafId = requestAnimationFrame(tick);
      }

      cancelAnimation() {
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
          this.track.classList.remove('is-animating');
        }
      }
    }
  );
}
